import React, { FC, useRef, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import Helmet from 'react-helmet';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { CarGallery, CarInfoProperties, Button } from '@azgaz/azgaz-ui';
import {
  useDispatch as useDispatchConnect,
  selectors as selectorsConnect,
  actions as actionsConnect,
  useSelector as useSelectorConnect,
} from '@lib/azgaz-ui/connect';
import { config, scrollTo, Retargeting, formatModel } from '@/utils';
import { useBrandCar } from '@/hooks';

import { getVehicles, getCarOptions, getImages } from '@/selectors';
import getCarDisclaimers from '@/selectors/getCarDisclaimers';
import { EVENT_ON_GA_TRACKING } from '@/constants';
import { GAEvents } from '@/types';
import { actions } from '../../store/vehicles';

import {
  Title,
  CarBackButton,
  CarBookForm,
  CarEquipment,
  CarHeaderFixed,
  CarTradeIn,
  CarCreditCalc,
  CarPriceDetails,
  CarPrice,
  CarOfficesMap,
} from '../index';
import outsideComponents from '../../outsideComponents';
import CarDisclaimers from '../CarDisclaimers';

import style from './style.module.scss';
import CarGroup from '../CarGroup';
import getSeoTags from '@/utils/getSeoTags';

const scrollToBook = scrollTo.bind(undefined, 'bookTitle');

const featureFlagCreditCalc = config.get('featureFlagCreditCalc');
const featureFlagTradeIn = config.get('featureFlagTradeIn');
const featureFlagFilteringByStatus = config.get('featureFlagFilteringByStatus');
const groupFilterEnable = config.get('groupFilterEnable');

const Car: FC = () => {
  const dispatch = useDispatch();
  const dispatchConnect = useDispatchConnect();
  const { id } = useParams<any>();
  const { DealerInfo } = outsideComponents;

  const instockEventDispatched = useRef(false);
  const retargetingSent = useRef(false);

  useEffect(() => {
    /* Убираем стандартный метатег description */
    const descriptionMeta = document.querySelector(
      'meta[itemprop="description"]'
    );

    if (descriptionMeta) descriptionMeta.remove();
  }, []);

  /* Получение и сброс автомобиля */
  useEffect(() => {
    window.scrollTo({ top: 0 });

    if (id) {
      dispatchConnect(actionsConnect.getCarFromStoreOrApi(id));
    }

    return () => {
      /** Удаляем текущий автомобиль чтобы при повторном заходе на страницу авто не было "моргания" */
      dispatchConnect(actionsConnect.clearCar());
    };
  }, [id]);

  const car = useSelectorConnect(selectorsConnect.getCar);

  const { vehicles } = useSelector(getVehicles);

  /* Получение каталога */
  useEffect(() => {
    if (!vehicles) {
      dispatch(actions.getVehicles());
    }
  }, []);

  const fields = [
    {
      source: 'root',
      id: 'color.name',
      title: 'Цвет',
    },
    {
      source: 'specification',
      id: 'engine',
    },
    {
      source: 'specification',
      id: 'drive',
    },
    {
      source: 'specification',
      id: 'transmission',
    },
    {
      source: 'root',
      id: 'grade',
      title: 'Комплектация',
    },
    {
      source: 'root',
      id: 'year',
      title: 'Год выпуска',
    },
    {
      source: 'root',
      id: 'mileage',
      title: 'Пробег',
    },
    // Скрыт в рамках задачи (https://git.perx.ru/suzuki/ring/-/issues/4286)
    // {
    //   source: 'root',
    //   id: 'vin',
    //   title: 'VIN номер',
    //   value: resolveCarVin(car?.vin),
    // },
  ];

  const store = useStore();

  const carOptions = getCarOptions(store.getState(), car);

  const brandCar = useBrandCar();

  const carDisclaimers = getCarDisclaimers(store.getState(), brandCar);

  const images = useMemo(() => getImages(car), [car?.id]);

  const renderGallery = (
    <CarGallery
      carouselSlider
      fullScreenSlider
      images={images}
      presets={{
        gallery: {
          id: '4',
        },
        'gallery-full': {
          id: '2',
        },
        'gallery-thumbnail': {
          id: '5',
        },
      }}
    />
  );

  const isJimny = useMemo(() => car?.model.toUpperCase() === 'JIMNY', [
    car?.model,
  ]);

  const onClickCarBook = useCallback(() => {
    scrollToBook();
  }, []);

  useEffect(() => {
    if (!retargetingSent.current && !!car) {
      const key = isJimny ? '10008' : '10006';
      Retargeting.onReady(['6011742f98657b070ebd92d8', key]);
      retargetingSent.current = true;
    }
  }, [isJimny, car]);

  useEffect(() => {
    if (brandCar && !instockEventDispatched.current) {
      document.dispatchEvent(
        new CustomEvent(EVENT_ON_GA_TRACKING, {
          detail: {
            gaEvent: GAEvents.instock,
            data: {
              productId: brandCar.vin,
              productvalue:
                brandCar.extra.carPriceDiscountedWithoutTradeInPrice,
              model: formatModel(brandCar.model || ''),
            },
          },
        })
      );
      instockEventDispatched.current = true;
    }
  }, [brandCar]);

  const renderGalleryPlaceholder = <div className="aui-gallery-placeholder" />;
  const seo = car && getSeoTags(car);
  const isGroupVisible = groupFilterEnable || featureFlagFilteringByStatus;

  return car ? (
    <div className="csp-car" itemScope itemType="http://schema.org/Product">
      {seo && (
        <Helmet>
          <title>{seo.title}</title>
          <meta name="title" content={seo.title} />
          <meta name="description" content={seo.description} />
        </Helmet>
      )}
      <CarHeaderFixed car={brandCar} disclaimers={carDisclaimers} />
      <div className="container">
        <Title className={cn('d-lg-none', style.title)} underline>
          {`${car.brand} ${car.model}`}
        </Title>
        <div className="row" id="car-info-properties">
          <div className="col-lg-7">
            {car.images?.length ? renderGallery : renderGalleryPlaceholder}

            {!!isGroupVisible && !!car.group && (
              <CarGroup status={car.group} className={style.carStatus} />
            )}
          </div>
          <div className="col-lg-5 mb-4 mb-lg-0">
            <Title className={cn('d-none d-lg-block', style.title)} underline>
              <span itemProp="name">{car.brand}</span>{' '}
              <span itemProp="model">{car.model}</span>
            </Title>

            <div itemProp="description">
              <CarInfoProperties fields={fields} car={car}/>
            </div>

            <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
              <CarPrice car={brandCar} disclaimers={carDisclaimers} />

              {DealerInfo && <DealerInfo innerProps={{
                  itemProp: 'seller',
                  itemScope: '',
                  itemType: 'https://schema.org/Organization'
                }} car={car} /> }
            </div>
          </div>
        </div>
        <div className={cn('row align-items-center', style.footer)}>
          <div className="col-lg-3 order-lg-1">
            <Button onClick={onClickCarBook}>Забронировать</Button>
          </div>
          <div className="col-lg-7">
            <CarBackButton path="/" className={style.backBtn} />
          </div>
        </div>

        {featureFlagTradeIn && (
          <CarTradeIn car={brandCar} disclaimers={carDisclaimers} />
        )}

        {featureFlagCreditCalc && (
          <CarCreditCalc car={brandCar} disclaimers={carDisclaimers} />
        )}

        <CarEquipment
          list={carOptions?.map(({ group, items }) => ({
            title: group,
            items,
          }))}
          title="Комплектация"
        />

        <CarPriceDetails car={brandCar} disclaimers={carDisclaimers} />

        <CarOfficesMap car={car} />

        <Title className="mb-4 pb-md-2" tag="h2" id="bookTitle">
          забронировать автомобиль
        </Title>
        <CarBookForm />
        <div className={cn(style.backWrap)}>
          <CarBackButton path="/" />
        </div>

        {carDisclaimers.size !== 0 && (
          <CarDisclaimers disclaimers={carDisclaimers} />
        )}
      </div>
    </div>
  ) : null;
};

export default Car;
